/**
 * SPA auth client — Cognito Hosted UI con Authorization Code + PKCE.
 *
 *   1. redirectToLogin() genera code_verifier, deriva code_challenge SHA256,
 *      guarda verifier + returnTo en localStorage, redirige a
 *      <cognitoDomain>/oauth2/authorize?...
 *   2. Cognito autentica al usuario y vuelve a <redirectUri>?code=...&state=...
 *   3. /callback invoca consumeCallback() que intercambia code → tokens
 *      contra <cognitoDomain>/oauth2/token y devuelve el returnTo.
 *
 * Espejo de `apps/builder-spa/src/auth/authClient.ts` con storage keys
 * propias (`zen_customer_*`) para evitar colisión si ambos SPAs comparten
 * dominio padre en algún entorno.
 */
import { config } from '../config.js';

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
}

export interface AuthClaims {
  sub: string | null;
  email: string | null;
  tenantId: string | null;
  /** cognito:groups (no usado por ZE backend, pero common Cognito). */
  groups: string[];
  /** custom:roles (CSV → array). Ej: ['admin'] o ['platform-ops']. */
  roles: string[];
  /** Display name si Cognito lo provee. */
  name: string | null;
}

const TOKENS_KEY = 'zen_customer_tokens_v1';
const VERIFIER_KEY = 'zen_customer_pkce_verifier_v1';
const RETURN_KEY = 'zen_customer_return_v1';

function parseJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(padded);
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function b64urlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function randomString(len = 64): string {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return b64urlEncode(arr);
}

async function sha256b64url(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return b64urlEncode(new Uint8Array(hash));
}

export function getTokens(): AuthTokens | null {
  try {
    const raw = sessionStorage.getItem(TOKENS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthTokens;
    if (!parsed.idToken) return null;
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      sessionStorage.removeItem(TOKENS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getClaims(): AuthClaims | null {
  const tokens = getTokens();
  if (!tokens) return null;
  const claims = parseJwt(tokens.idToken);
  const rawRoles = claims['custom:roles'];
  const roles = Array.isArray(rawRoles)
    ? (rawRoles as string[])
    : typeof rawRoles === 'string'
      ? rawRoles.split(',').map((r) => r.trim()).filter(Boolean)
      : [];
  return {
    sub: (claims.sub as string) ?? null,
    email: (claims.email as string) ?? null,
    tenantId: (claims['custom:tenant_id'] as string) ?? null,
    groups: (claims['cognito:groups'] as string[]) ?? [],
    roles,
    name:
      (claims.name as string) ??
      (claims['given_name'] as string) ??
      null,
  };
}

export function isAuthenticated(): boolean {
  return getTokens() !== null;
}

export async function redirectToLogin(returnTo?: string): Promise<void> {
  const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
  const verifier = randomString(64);
  const challenge = await sha256b64url(verifier);
  localStorage.setItem(VERIFIER_KEY, verifier);
  localStorage.setItem(RETURN_KEY, target);

  const url = new URL(`${config.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.cognitoClientId);
  url.searchParams.set('redirect_uri', config.redirectUri);
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', encodeURIComponent(target));
  window.location.assign(url.toString());
}

/**
 * Intercambia code → tokens via /oauth2/token. Devuelve returnTo.
 * @throws Error si el code es inválido o el exchange falla.
 */
export async function consumeCallback(search: string = window.location.search): Promise<string> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  if (!code) throw new Error('callback.missingCode');
  const verifier = localStorage.getItem(VERIFIER_KEY) ?? sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('callback.missingVerifier');

  const tokenUrl = `${config.cognitoDomain}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.cognitoClientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`callback.tokenExchangeFailed: ${res.status} ${text}`);
  }
  const json = (await res.json()) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
  if (!json.id_token || !json.access_token) {
    throw new Error('callback.tokenResponseInvalid');
  }
  const tokens: AuthTokens = {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token ?? null,
    expiresAt: Date.now() + (json.expires_in ?? 3600) * 1000,
  };
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  localStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  const stateParam = params.get('state');
  const returnTo =
    stateParam
      ? decodeURIComponent(stateParam)
      : (localStorage.getItem(RETURN_KEY) ?? sessionStorage.getItem(RETURN_KEY) ?? '/');
  localStorage.removeItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return returnTo;
}

export function logout(): void {
  sessionStorage.removeItem(TOKENS_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  localStorage.removeItem(VERIFIER_KEY);
  localStorage.removeItem(RETURN_KEY);
  const url = new URL(`${config.cognitoDomain}/logout`);
  url.searchParams.set('client_id', config.cognitoClientId);
  url.searchParams.set('logout_uri', config.logoutUri);
  window.location.assign(url.toString());
}

export function authorizationHeader(): string | null {
  const tokens = getTokens();
  return tokens ? `Bearer ${tokens.idToken}` : null;
}

export function redirectToLoginSync(returnTo?: string): void {
  redirectToLogin(returnTo).catch((err: unknown) => {
    // eslint-disable-next-line no-console
    console.warn('redirectToLogin failed', err);
  });
}
